import { Check, PickRoles } from '@/components/ui/icons';
import { useState } from 'react';
import { errorMessage } from '@/api/client';
import { serverApi } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/Feedback';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { useServerRoles, useServers } from '@/stores/servers';
import { toast } from '@/stores/ui';

/** Pick your roles: the roles a server lets members give themselves (often unlocking channels). */
export function PickRolesModal({ serverId, onClose }: { serverId: string; onClose: () => void }) {
  const server = useServers((s) => s.servers[serverId]);
  const roles = useServerRoles(serverId).filter((r) => r.selfAssignable && !r.isDefault);
  const [busy, setBusy] = useState<string | null>(null);
  if (!server) return null;
  const mine = new Set(server.me.roleIds);

  const toggle = async (roleId: string) => {
    const on = !mine.has(roleId);
    setBusy(roleId);
    try {
      const member = await serverApi.pickRole(serverId, roleId, on);
      useServers.getState().upsertMember(member);
      useServers.getState().patchServer(serverId, { me: { ...server.me, roleIds: member.roleIds } });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Pick your roles"
      description={`Choose what you’re into in ${server.name}. Some roles open up extra channels. Change them any time.`}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {roles.length === 0 ? (
        <EmptyState icon={<PickRoles size={26} />} title="Nothing to pick yet" body="This server hasn’t set up any roles you can pick yourself." />
      ) : (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {roles.map((role) => {
            const on = mine.has(role.id);
            return (
              <li key={role.id}>
                <button
                  type="button"
                  onClick={() => void toggle(role.id)}
                  disabled={busy === role.id}
                  aria-pressed={on}
                  className={cn(
                    'flex h-full w-full items-start gap-3 rounded-2xl border p-3 text-left transition-colors disabled:opacity-60',
                    on ? 'border-accent bg-accent-soft' : 'border-line hover:border-line-strong hover:bg-hover/40',
                  )}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sunken text-lg" aria-hidden>
                    {role.emoji || '🏷️'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold" style={{ color: role.color ?? undefined }}>
                      {role.name}
                    </span>
                    {role.description && <span className="mt-0.5 block text-xs text-fg-muted">{role.description}</span>}
                  </span>
                  <span
                    className={cn('mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors', on ? 'border-accent bg-accent text-accent-fg' : 'border-line-strong')}
                    aria-hidden
                  >
                    {on && <Check size={12} />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
